import { memo } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS,
  WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS_TOGGLE,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionVirtualRows';
import { type WorkspaceAgentSessionBucket } from '~/components/v2/components/workspace/workspace-agent-panel/utils/classifyWorkspaceAgentSessionBucket';

export type WorkspaceAgentPanelMyTasksSectionHeaderSection = WorkspaceAgentSessionBucket | 'automations';

export type WorkspaceAgentPanelMyTasksSectionHeaderProps = {
  section: WorkspaceAgentPanelMyTasksSectionHeaderSection;
  count: number;
  isCollapsible: boolean;
  isExpanded: boolean;
  isFirst: boolean;
  label: string;
  onToggle?: () => void;
};

const getWorkspaceAgentPanelMyTasksSectionListTestId = (section: WorkspaceAgentPanelMyTasksSectionHeaderSection) => {
  switch (section) {
    case 'needs-response':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_NEEDS_RESPONSE';
    case 'in-progress':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_IN_PROGRESS';
    case 'all-tasks':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_ALL_TASKS';
    case 'archived':
      return 'WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_ARCHIVED';
    case 'automations':
      return WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS;
  }
};

const getWorkspaceAgentPanelMyTasksSectionToggleTestId = (section: WorkspaceAgentPanelMyTasksSectionHeaderSection) => {
  if (section === 'automations') {
    return WORKSPACE_AGENT_PANEL_MY_TASKS_LIST_AUTOMATIONS_TOGGLE;
  }

  return undefined;
};

const getCountUnitSrOnlyLabel = (section: WorkspaceAgentPanelMyTasksSectionHeaderSection) => {
  if (section === 'automations') {
    return 'automations';
  }

  return 'tasks';
};

export const WorkspaceAgentPanelMyTasksSectionHeader = memo(
  ({
    section,
    count,
    isCollapsible,
    isExpanded,
    isFirst,
    label,
    onToggle,
  }: WorkspaceAgentPanelMyTasksSectionHeaderProps) => {
    const toggleTestId = getWorkspaceAgentPanelMyTasksSectionToggleTestId(section);
    const countUnitLabel = getCountUnitSrOnlyLabel(section);

    const heading = (
      <span className="flex min-w-0 items-center gap-1.5">
        {isCollapsible ? (
          <ChevronDownIcon
            aria-hidden
            className={cn(
              'shrink-0 ads:text-icon-subtle text-gray-8 transition-transform',
              !isExpanded && '-rotate-90',
            )}
            size="xs"
          />
        ) : null}
        <span>{label}</span>
        <span className="font-normal ads:text-metadata text-gray-8 tabular-nums">
          {count}
          <span className="sr-only"> {countUnitLabel}</span>
        </span>
      </span>
    );

    return (
      <div className={cn(!isFirst && 'pt-5')} data-testid={getWorkspaceAgentPanelMyTasksSectionListTestId(section)}>
        <h3
          className={cn(
            'pb-3',
            !isCollapsible && 'font-semibold ads:text-metadata text-10 text-gray-9 uppercase tracking-[0.4px]',
          )}
        >
          {isCollapsible ? (
            <button
              aria-expanded={isExpanded}
              className="w-full text-left font-semibold ads:text-metadata text-10 text-gray-9 uppercase tracking-[0.4px] focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
              data-testid={toggleTestId}
              onClick={onToggle}
              type="button"
            >
              {heading}
            </button>
          ) : (
            heading
          )}
        </h3>
      </div>
    );
  },
);

WorkspaceAgentPanelMyTasksSectionHeader.displayName = 'WorkspaceAgentPanelMyTasksSectionHeader';
